import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    ACCESS_MODE,
    Advertisement,
    AdvertisementAsset,
    Campaign,
    CampaignAdvertisement,
    Client,
    LocationGroupCampaignSearch,
    MainLocationAllocation,
    SlotResults,
    User,
} from '@horasys/bellplus/common/types';
import { advertisementDetailsbyClientId, getCampaignAdvertisementDetails, getPrimaryAsset } from 'services/advertising.service';
import { MatBreakPoint, MatDirection, MatJustify } from '../common';
import { SET_PROGRESS } from './progressSlice';
import { deleteCampaign, deleteSlotAllocation, getAllCampaigns, getAllLocationDetails, getAreas, getAutoSlotAllocation, getCampaignDetails, getCities, getLocationGroupManualSlotAllocation, getLocationGroupSearchResults, getManualSlotAllocation, submitCampaignDetails, updateCampaignDetails } from 'services/campaigns.service';
import { SEND_MESSAGE } from './errorHandlingSlice';
import { GET_ADVERTISEMENT_DATA_LIST_BY_CLIENT_ID } from './advertisementSlice';
import history from 'router/history';

export interface CampaignAdvertisementUIState {
    showConfirm: boolean;
    toolbarMode: MatDirection;
    toolbarColSize: MatBreakPoint;
    mainContainerAlign: MatJustify;
    isNewlyAllocatedSlots: boolean;
    tabValue: number;
    isChooseDevicesPopupOpen: boolean;
    isChooseDevicesViewMode: boolean;
}

const initialCampaignAdvertisementUIState: CampaignAdvertisementUIState = {
    showConfirm: false,
    toolbarMode: 'column',
    toolbarColSize: 4,
    mainContainerAlign: 'flex-start',
    isNewlyAllocatedSlots: false,
    tabValue: 0,
    isChooseDevicesPopupOpen: false,
    isChooseDevicesViewMode: false,
};

const addDays = (date: Date, days: number): Date => {
    date.setDate(date.getDate() + days);
    return date;
};
const clientObj: Client = {} as Client;
clientObj.name = '';
clientObj.companyName = '';
const userObj: Partial<User> = {} as Partial<User>;
userObj.id = 0;
userObj.username = '';

const emptyAdvertisement: Advertisement = {} as Advertisement;
const emptyCampaignAdvertisement: CampaignAdvertisement = {
    id: 0,
    advertisement: emptyAdvertisement,
    userBudget: 0,
    calculatedBudget: 0,
    isAuto: false,
    noOfTimes: 1,
    startDate: addDays(new Date(), 0),
    endDate: addDays(new Date(), 0),
    uuid: '',
    searchLocations: [],
    selectedLocationGroups: [],
    searchLocationGroups: [],
    selectedAreas: [],
    typeOfSearch: 'location',
    selectedCities: [],
    selectedLocations: [],
    index: -1,
};
const emptyCampaignObject: Campaign = {
    id: 0,
    name: '',
    originalName: '',
    salesPersonUser: userObj,
    client: clientObj,
    price: 0,
    payments: [],
    campaignAdvertisements: [],
    status: '',
    paymentReceived: 0,
    uuids: [],
    campaignPaymentDetailsResponse: [],
    campaignAdvertisementDetailsResponse: [],
};
const emptySlotResults: SlotResults = {} as SlotResults;
emptySlotResults.budget = 0;
emptySlotResults.locations = [];
emptySlotResults.uuid = '';

export interface CampaignState {
    list: Array<any>;
    error: any;
    loading: boolean;
    mode: ACCESS_MODE;
    selectedId: number;
    campaign: Campaign;
    totalElements: number;
    cities: any;
    areas: any;
    mainLocationSearchResults: Array<MainLocationAllocation>;
    locationGroupSearchResults: Array<any>;
    currentSlotResults: SlotResults;
    campaignAdvertisementForm: CampaignAdvertisement;
    campaignAdvertisementState: CampaignAdvertisementUIState;
    campaignAdvertisements: Array<Advertisement>;

}
const initialState: CampaignState = {
    list: [],
    error: null,
    loading: false,
    mode: 'CREATE',
    selectedId: 0,
    totalElements: 0,
    campaign: emptyCampaignObject,
    cities: [],
    areas: [],
    mainLocationSearchResults: [],
    locationGroupSearchResults: [],
    currentSlotResults: emptySlotResults,
    campaignAdvertisementForm: emptyCampaignAdvertisement,
    campaignAdvertisementState: initialCampaignAdvertisementUIState,
    campaignAdvertisements: [],

};

const campaignSlice = createSlice({
    name: 'campaign',
    initialState,
    reducers: {
        UPDATE_CAMPAIGN_MODE: (state, action: PayloadAction<any>): CampaignState => {
            if (action.payload.mode !== 'CREATE') {
                return {
                    ...state,
                    mode: action.payload.mode,
                    selectedId: action.payload.selectedId,
                };
            } else {
                return {
                    ...state,
                    campaign: emptyCampaignObject,
                    campaignAdvertisementForm: emptyCampaignAdvertisement,
                    campaignAdvertisementState: initialCampaignAdvertisementUIState,
                    cities: [],
                    areas: [],
                    currentSlotResults: emptySlotResults,
                };
            }
        },
        GET_CAMPAIGN_DATA_LIST: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                list: action.payload.list,
                totalElements: action.payload.totalElements,
            };
        },
        GET_CITIES: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                cities: action.payload,
            };
        },
        GET_AREAS_BY_CITIES: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                areas: action.payload,
            };
        },
        DELETE_CAMPAIGN_ADVERTISEMENT: (state, action: PayloadAction<any>): CampaignState => {
            const updatedCampaign = { ...state.campaign };
            const updateCampaignAdvertisements = updatedCampaign.campaignAdvertisements.filter(
                (campAdvertisement: CampaignAdvertisement) => {
                    if (campAdvertisement.index !== action.payload) {
                        return campAdvertisement;
                    } else {
                        return false;
                    }
                },
            );
            updatedCampaign.campaignAdvertisements = [...updateCampaignAdvertisements];
            let price = 0;
            updatedCampaign.campaignAdvertisements.forEach(
                (campaignAdvertisement: CampaignAdvertisement) => {
                    price = price + campaignAdvertisement.calculatedBudget;
                },
            );
            updatedCampaign.price = price;
            return {
                ...state,
                campaign: { ...updatedCampaign },
            };
        },
        GET_CAMPAIGN_ADVERTISEMENTS: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaignAdvertisements: action.payload,
            };
        },
        ADD_CAMPAIGN_ADVERTISEMENT: (state, action: PayloadAction<any>): CampaignState => {
            if (state.campaignAdvertisementForm.index === -1) {
                const updatedCampaignAdvertisement: CampaignAdvertisement = {
                    ...state.campaignAdvertisementForm,
                };
                updatedCampaignAdvertisement.index =
                    state.campaign.campaignAdvertisements.length;
                const advertisementAsset: AdvertisementAsset = getPrimaryAsset(
                    updatedCampaignAdvertisement.advertisement,
                );
                if (advertisementAsset.type === 'video') {
                    updatedCampaignAdvertisement.previewUrl = advertisementAsset.url;
                    updatedCampaignAdvertisement.thumbnailUrl =
                        advertisementAsset.thumbnailAsset?.url;
                } else if (advertisementAsset.type === 'image') {
                    updatedCampaignAdvertisement.previewUrl = advertisementAsset.url;
                    updatedCampaignAdvertisement.thumbnailUrl = advertisementAsset.url;
                } else if (advertisementAsset.type === 'multipleImages') {
                    updatedCampaignAdvertisement.previewUrl =
                        advertisementAsset.children &&
                            advertisementAsset.children?.length > 0
                            ? advertisementAsset.children[0].url
                            : '';
                    updatedCampaignAdvertisement.thumbnailUrl =
                        advertisementAsset.children &&
                            advertisementAsset.children?.length > 0
                            ? advertisementAsset.children[0].url
                            : '';
                }

                let price = 0;
                state.campaign.campaignAdvertisements.forEach(
                    (campaignAdvertisement: CampaignAdvertisement) => {
                        price = price + campaignAdvertisement.calculatedBudget;
                    },
                );
                price = price + updatedCampaignAdvertisement.calculatedBudget;
                const updatedCampaign = { ...state.campaign };
                updatedCampaign.campaignAdvertisements = [
                    ...updatedCampaign.campaignAdvertisements,
                    updatedCampaignAdvertisement,
                ];
                updatedCampaign.price = price;
                return {
                    ...state,
                    campaign: updatedCampaign,
                };
            } else {
                const updatedCampaignAdvertisement: CampaignAdvertisement = {
                    ...state.campaignAdvertisementForm,
                };
                const advertisementAsset: AdvertisementAsset = getPrimaryAsset(
                    updatedCampaignAdvertisement,
                );
                if (advertisementAsset.type === 'video') {
                    updatedCampaignAdvertisement.previewUrl = advertisementAsset.url;
                    updatedCampaignAdvertisement.thumbnailUrl =
                        advertisementAsset.thumbnailAsset?.url;
                } else if (advertisementAsset.type === 'image') {
                    updatedCampaignAdvertisement.previewUrl = advertisementAsset.url;
                    updatedCampaignAdvertisement.thumbnailUrl = advertisementAsset.url;
                } else if (advertisementAsset.type === 'multipleImages') {
                    updatedCampaignAdvertisement.previewUrl =
                        advertisementAsset.children &&
                            advertisementAsset.children?.length > 0
                            ? advertisementAsset.children[0].url
                            : '';
                    updatedCampaignAdvertisement.thumbnailUrl =
                        advertisementAsset.children &&
                            advertisementAsset.children?.length > 0
                            ? advertisementAsset.children[0].url
                            : '';
                }

                const updatedCampaignAdvertisements = state.campaign.campaignAdvertisements.map(
                    (campaignAd: CampaignAdvertisement) => {
                        if (campaignAd.index === updatedCampaignAdvertisement.index) {
                            return updatedCampaignAdvertisement;
                        } else {
                            return campaignAd;
                        }
                    },
                );
                const updatedCampaign = { ...state.campaign };
                updatedCampaign.campaignAdvertisements = [
                    ...updatedCampaignAdvertisements,
                ];
                let price = 0;
                state.campaign.campaignAdvertisements.forEach(
                    (campaignAdvertisement: CampaignAdvertisement) => {
                        price = price + campaignAdvertisement.calculatedBudget;
                    },
                );
                price = price + updatedCampaignAdvertisement.calculatedBudget;

                updatedCampaign.campaignAdvertisements = [
                    ...updatedCampaign.campaignAdvertisements,
                    updatedCampaignAdvertisement,
                ];
                updatedCampaign.price = price;
                return {
                    ...state,
                    campaign: { ...updatedCampaign },
                };
            }
        },
        GET_MAIN_LOCATIONS_SEARCH_BY_AREA: (state, action: PayloadAction<any>): CampaignState => {
            const results: Array<MainLocationAllocation> = action.payload;
            const updatedResult: Array<MainLocationAllocation> = results.map(
                (location: MainLocationAllocation) => {
                    const updatedLocationObject: MainLocationAllocation = {
                        ...location,
                        selectedDevices: 1,
                        noOfTimes: 1,
                        added: false,
                    };
                    return updatedLocationObject;
                },
            );
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocations: updatedResult,
                },
            };
        },
        GET_LOCATION_GROUP_SEARCH_BY_CITY: (state, action: PayloadAction<any>): CampaignState => {
            const results: Array<LocationGroupCampaignSearch> = action.payload;
            const updatedResult: Array<LocationGroupCampaignSearch> = results.map(
                (locationGroup: LocationGroupCampaignSearch) => {
                    const updatedLocationGroupObject: LocationGroupCampaignSearch = {
                        ...locationGroup,
                        selectedDevices: 1,
                        noOfTimes: 1,
                        added: false,
                    };
                    return updatedLocationGroupObject;
                },
            );
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocationGroups: updatedResult,
                },
            };
        },
        UPDATE_AUTO_RESULT_UI_STATE: (state, action: PayloadAction<any>): CampaignState => {
            if (action.payload) {
                return {
                    ...state,
                    campaignAdvertisementState: {
                        ...state.campaignAdvertisementState,
                        showConfirm: true,
                        toolbarMode: 'row',
                        toolbarColSize: 12,
                        mainContainerAlign: 'center',
                        isNewlyAllocatedSlots: true,
                    },
                };
            } else {
                return {
                    ...state,
                    campaignAdvertisementState: {
                        ...state.campaignAdvertisementState,
                        showConfirm: false,
                        isNewlyAllocatedSlots: false,
                    },
                };
            }

        },
        UPDATE_CAMPAIGN_AD_UI_STATE: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaignAdvertisementState: {
                    ...state.campaignAdvertisementState,
                    ...action.payload,
                },
            };

        },
        GET_LOCATION_GROUP_MANUAL_RESULT: (state, action: PayloadAction<any>): CampaignState => {
            if (action.payload.locations.length) {
                return {
                    ...state,
                    currentSlotResults: action.payload,
                    campaignAdvertisementForm: {
                        ...state.campaignAdvertisementForm,
                        selectedLocationGroups: [...action.payload.locations],
                        calculatedBudget: action.payload.budget,
                        uuid: action.payload.uuid,
                    },
                    campaignAdvertisementState: {
                        ...state.campaignAdvertisementState,
                        tabValue: 1,
                        showConfirm: true,
                        toolbarColSize: 12,
                        mainContainerAlign: 'center',
                        toolbarMode: 'row',
                        isNewlyAllocatedSlots: true,
                    },
                };
            } else {
                return {
                    ...state,
                    campaignAdvertisementForm: {
                        ...state.campaignAdvertisementForm,
                        selectedLocationGroups: [],
                        calculatedBudget: 0,
                        uuid: action.payload.uuid,
                    },
                    campaignAdvertisementState: {
                        ...state.campaignAdvertisementState,
                        tabValue: 0,
                        showConfirm: false,
                        isNewlyAllocatedSlots: false,
                    },
                    currentSlotResults: emptySlotResults,
                };
            }

        },
        GET_MAIN_LOCATION_MANUAL_RESULT: (state, action: PayloadAction<any>): CampaignState => {
            if (action.payload.locations.length) {
                return {
                    ...state,
                    currentSlotResults: action.payload,
                    campaignAdvertisementForm: {
                        ...state.campaignAdvertisementForm,
                        selectedLocations: [...action.payload.locations],
                        calculatedBudget: action.payload.budget,
                        uuid: action.payload.uuid,
                    },
                    campaignAdvertisementState: {
                        ...state.campaignAdvertisementState,
                        tabValue: 1,
                        showConfirm: true,
                        toolbarColSize: 12,
                        mainContainerAlign: 'center',
                        toolbarMode: 'row',
                        isNewlyAllocatedSlots: true,
                    },
                };
            } else {
                return {
                    ...state,
                    campaignAdvertisementForm: {
                        ...state.campaignAdvertisementForm,
                        selectedLocations: [],
                        calculatedBudget: 0,
                        uuid: action.payload.uuid,
                    },
                    campaignAdvertisementState: {
                        ...state.campaignAdvertisementState,
                        tabValue: 0,
                        showConfirm: false,
                        isNewlyAllocatedSlots: false,
                    },
                    currentSlotResults: emptySlotResults,
                };
            }

        },
        RESET_CAMPAIGN_STATE: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaign: {
                    ...emptyCampaignObject,
                },
                campaignAdvertisementForm: {
                    ...emptyCampaignAdvertisement,
                },
                campaignAdvertisementState: {
                    ...initialCampaignAdvertisementUIState,
                },
                mode: 'CREATE',
                selectedId: 0,
            };
        },
        GET_CAMPAIGN_BY_ID: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaign: {
                    ...action.payload,
                },
            };
        },
        GET_MAIN_LOCATION_AUTO_RESULT: (state, action: PayloadAction<any>): CampaignState => {
            if (action.payload.locations.length) {
                return {
                    ...state,
                    currentSlotResults: action.payload,
                    campaignAdvertisementForm: {
                        ...state.campaignAdvertisementForm,
                        calculatedBudget: action.payload.budget,
                        uuid: action.payload.uuid,
                        selectedLocations: [...action.payload.locations],
                    },
                };
            } else {
                return {
                    ...state,
                    campaignAdvertisementForm: {
                        ...state.campaignAdvertisementForm,
                        calculatedBudget: 0,
                        uuid: action.payload.uuid,
                        selectedLocations: [],
                    },
                };
            }
        },
        UPDATE_CAMPAIGN_FORM: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaign: { ...state.campaign, ...action.payload },
            };
        },
        UPDATE_CAMPAIGN_ADVERTISEMENT_FORM: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    ...action.payload,
                },
            };
        },
        RESET_CAMPAIGN_ADVERTISEMENT_FORM: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...emptyCampaignAdvertisement,
                },
                campaignAdvertisementState: {
                    ...initialCampaignAdvertisementUIState,
                },
            };
        },
        RESET_CAMPAIGN_ADVERTISEMENT_UI_STATE: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
                campaignAdvertisementState: {
                    ...initialCampaignAdvertisementUIState,
                },
            };
        },

        CAMPAIGN_MANUAL_ADD_LOCATION: (
          state,
          action: PayloadAction<any>,
        ): any => {
          try {
            const location: MainLocationAllocation ={ ...action.payload};
            location.added = true;
            const searchedLocations = state.campaignAdvertisementForm.searchLocations;
            const updatedSearchedLocations: Array<MainLocationAllocation> =
              searchedLocations.map((locationInfo: MainLocationAllocation) => {
                if (locationInfo.id === location.id) {
                  return location;
                } else {
                  return locationInfo;
                }
              });
            return {
              ...state,
              campaignAdvertisementForm: {
                ...state.campaignAdvertisementForm,
                searchLocations: updatedSearchedLocations,
              },
            };
          } catch (err) {
            console.log(err);
          }
        },
        CAMPAIGN_MANUAL_ADD_LOCATION_GROUP: (
          state,
          action: PayloadAction<any>,
        ): CampaignState => {
          const location: LocationGroupCampaignSearch = {...action.payload};
          location.added = true;
          const searchedLocationsGroups =
            state.campaignAdvertisementForm.searchLocationGroups;
          const updatedSearchedLocationsGroups: Array<LocationGroupCampaignSearch> =
            searchedLocationsGroups.map(
              (locationInfo: LocationGroupCampaignSearch) => {
                if (locationInfo.id === location.id) {
                  return location;
                } else {
                  return locationInfo;
                }
              },
            );
          return {
            ...state,
            campaignAdvertisementForm: {
              ...state.campaignAdvertisementForm,
              searchLocationGroups: updatedSearchedLocationsGroups,
            },
          };
        },

        CAMPAIGN_MANUAL_DELETE_LOCATION: (
          state,
          action: PayloadAction<any>,
        ): CampaignState => {
          const location: MainLocationAllocation = {...action.payload};
          location.added = false;
          const searchedLocations = state.campaignAdvertisementForm.searchLocations;
          const updatedSearchedLocations: Array<MainLocationAllocation> =
            searchedLocations.map((locationInfo: MainLocationAllocation) => {
              if (locationInfo.id === location.id) {
                return location;
              } else {
                return locationInfo;
              }
            });
          return {
            ...state,
            campaignAdvertisementForm: {
              ...state.campaignAdvertisementForm,
              searchLocations: updatedSearchedLocations,
            },
          };
        },

        CAMPAIGN_MANUAL_DELETE_LOCATION_GROUP: (
          state,
          action: PayloadAction<any>,
        ): CampaignState => {
          const location: LocationGroupCampaignSearch = {...action.payload};
          location.added = false;
          const searchedLocationGroups =
            state.campaignAdvertisementForm.searchLocationGroups;
          const updatedSearchedLocationGroups: Array<LocationGroupCampaignSearch> =
            searchedLocationGroups.map(
              (locationInfo: LocationGroupCampaignSearch) => {
                if (locationInfo.id === location.id) {
                  return location;
                } else {
                  return locationInfo;
                }
              },
            );
          return {
            ...state,
            campaignAdvertisementForm: {
              ...state.campaignAdvertisementForm,
              searchLocationGroups: updatedSearchedLocationGroups,
            },
          };
        },
        UPDATE_LOCATION_MANUAL_NO_TIMES: (state, action: PayloadAction<any>): CampaignState => {
            const location: MainLocationAllocation = action.payload.location;
            location.noOfTimes = action.payload.noOfTimes;
            const searchedLocations = state.campaignAdvertisementForm.searchLocations;
            const updatedSearchedLocations: Array<MainLocationAllocation> = searchedLocations.map(
                (locationInfo: MainLocationAllocation) => {
                    if (locationInfo.id === location.id) {
                        return location;
                    } else {
                        return locationInfo;
                    }
                },
            );
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocations: updatedSearchedLocations,
                },
            };
        },
        UPDATE_LOCATION_GROUP_MANUAL_NO_TIMES: (state, action: PayloadAction<any>): CampaignState => {
            const locationGroup: LocationGroupCampaignSearch =
                action.payload.locationGroup;
            locationGroup.noOfTimes = action.payload.noOfTimes;
            const searchedLocationGroups =
                state.campaignAdvertisementForm.searchLocationGroups;
            const updatedSearchedLocationGroups: Array<LocationGroupCampaignSearch> = searchedLocationGroups.map(
                (locationInfo: LocationGroupCampaignSearch) => {
                    if (locationInfo.id === locationGroup.id) {
                        return locationGroup;
                    } else {
                        return locationInfo;
                    }
                },
            );
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocationGroups: updatedSearchedLocationGroups,
                },
            };
        },
        UPDATE_LOCATION_SELECT_ALL_DEVICES: (state, action: PayloadAction<any>): CampaignState => {
            const location: MainLocationAllocation = action.payload;
            if (!location.selectAllDevices) {
                location.selectedDevices = action.payload.availableDevices;
                location.selectAllDevices = true;
            } else {
                location.selectAllDevices = false;
            }
            const searchedLocations = state.campaignAdvertisementForm.searchLocations;
            const updatedSearchedLocations: Array<MainLocationAllocation> = searchedLocations.map(
                (locationInfo: MainLocationAllocation) => {
                    if (locationInfo.id === location.id) {
                        return location;
                    } else {
                        return locationInfo;
                    }
                },
            );

            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocations: updatedSearchedLocations,
                },
            };
        },
        UPDATE_LOCATION_GROUP_SELECT_ALL_DEVICES: (state, action: PayloadAction<any>): CampaignState => {
            const locationGroup: LocationGroupCampaignSearch = action.payload;
            if (!locationGroup.selectAllDevices) {
                locationGroup.selectedDevices = action.payload.availableDevices;
                locationGroup.selectAllDevices = true;
            } else {
                locationGroup.selectAllDevices = false;
            }

            const searchedLocationGroups =
                state.campaignAdvertisementForm.searchLocationGroups;
            const updatedSearchedLocations: Array<LocationGroupCampaignSearch> = searchedLocationGroups.map(
                (locationInfo: LocationGroupCampaignSearch) => {
                    if (locationInfo.id === locationGroup.id) {
                        return locationGroup;
                    } else {
                        return locationInfo;
                    }
                },
            );
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocationGroups: updatedSearchedLocations,
                },
            };
        },
        UPDATE_LOCATION_MANUAL_SELECTED_DEVICES: (state, action: PayloadAction<any>): CampaignState => {
            const location: MainLocationAllocation = action.payload.location;
            location.selectedDevices = action.payload.selectedDevices;
            const searchedLocations = state.campaignAdvertisementForm.searchLocations;
            const updatedSearchedLocations: Array<MainLocationAllocation> = searchedLocations.map(
                (locationInfo: MainLocationAllocation) => {
                    if (locationInfo.id === location.id) {
                        return location;
                    } else {
                        return locationInfo;
                    }
                },
            );
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocations: updatedSearchedLocations,
                },
            };
        },
        UPDATE_LOCATION_GROUP_MANUAL_SELECTED_DEVICES: (state, action: PayloadAction<any>): CampaignState => {
            const locationGroup: LocationGroupCampaignSearch =
                action.payload.locationGroup;
            locationGroup.selectedDevices = action.payload.selectedDevices;
            const searchedLocationGroups =
                state.campaignAdvertisementForm.searchLocationGroups;
            const updatedSearchedLocations: Array<LocationGroupCampaignSearch> = searchedLocationGroups.map(
                (locationInfo: LocationGroupCampaignSearch) => {
                    if (locationInfo.id === locationGroup.id) {
                        return locationGroup;
                    } else {
                        return locationInfo;
                    }
                },
            );
            return {
                ...state,
                campaignAdvertisementForm: {
                    ...state.campaignAdvertisementForm,
                    searchLocationGroups: updatedSearchedLocations,
                },
            };
        },
        CAMPAIGN_MANUAL_LOCATION_BOOK_SLOT: (state, action: PayloadAction<any>): CampaignState => {
            return {
                ...state,
            };
        },

    }
})

export const { UPDATE_CAMPAIGN_MODE, GET_CAMPAIGN_DATA_LIST, GET_CITIES, GET_AREAS_BY_CITIES, DELETE_CAMPAIGN_ADVERTISEMENT, GET_CAMPAIGN_ADVERTISEMENTS, ADD_CAMPAIGN_ADVERTISEMENT, GET_MAIN_LOCATIONS_SEARCH_BY_AREA, GET_LOCATION_GROUP_SEARCH_BY_CITY, UPDATE_AUTO_RESULT_UI_STATE, UPDATE_CAMPAIGN_AD_UI_STATE, GET_LOCATION_GROUP_MANUAL_RESULT, GET_MAIN_LOCATION_MANUAL_RESULT, RESET_CAMPAIGN_STATE, GET_CAMPAIGN_BY_ID, GET_MAIN_LOCATION_AUTO_RESULT, UPDATE_CAMPAIGN_FORM, UPDATE_CAMPAIGN_ADVERTISEMENT_FORM, RESET_CAMPAIGN_ADVERTISEMENT_FORM
    , RESET_CAMPAIGN_ADVERTISEMENT_UI_STATE, CAMPAIGN_MANUAL_ADD_LOCATION, CAMPAIGN_MANUAL_ADD_LOCATION_GROUP, CAMPAIGN_MANUAL_DELETE_LOCATION, CAMPAIGN_MANUAL_DELETE_LOCATION_GROUP, UPDATE_LOCATION_MANUAL_NO_TIMES, UPDATE_LOCATION_GROUP_MANUAL_NO_TIMES, UPDATE_LOCATION_SELECT_ALL_DEVICES, UPDATE_LOCATION_GROUP_SELECT_ALL_DEVICES, UPDATE_LOCATION_MANUAL_SELECTED_DEVICES, UPDATE_LOCATION_GROUP_MANUAL_SELECTED_DEVICES } = campaignSlice.actions
export default campaignSlice.reducer



export const getMainLocationAutoResult = (requestObject: any): any => async (
    dispatch: any,
) => {
    try {
        dispatch(SET_PROGRESS(true));
        const slotResults: SlotResults = await getAutoSlotAllocation(requestObject);
        if (slotResults.locations.length) {
            dispatch(UPDATE_AUTO_RESULT_UI_STATE(true));
            dispatch(GET_MAIN_LOCATION_AUTO_RESULT(slotResults));
        } else {
            dispatch(UPDATE_AUTO_RESULT_UI_STATE(false));
            dispatch(SEND_MESSAGE({message:'Not Slots available', type:'warning'}));
        }
        dispatch(SET_PROGRESS(false));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};

export const deleteCampaignManualLocationAction =
	(location: MainLocationAllocation) => async (dispatch: any) => {
		try {
			dispatch(CAMPAIGN_MANUAL_DELETE_LOCATION(location));
		} catch (error: any) {
		}
	};

export const updateMainLocationSelectedDevices =
	(location: MainLocationAllocation, selectedDevices: number) =>
	async (dispatch: any) => {
		try {
			dispatch(
				UPDATE_LOCATION_MANUAL_SELECTED_DEVICES({
					location: location,
					selectedDevices: selectedDevices,
				}),
			);
		} catch (error: any) {
		}
	};


  export const updateMainLocationGroupSelectedDevices =
	(location: LocationGroupCampaignSearch, selectedDevices: number) =>
	async (dispatch: any) => {
		try {
			dispatch(
				UPDATE_LOCATION_GROUP_MANUAL_SELECTED_DEVICES({
					locationGroup: location,
					selectedDevices: selectedDevices,
				}),
			);
		} catch (error: any) {
		}
	};


  export const updateMainLocationGroupSelectAllDevices =
	(locationGroup: LocationGroupCampaignSearch) => async (dispatch: any) => {
		try {
			dispatch(UPDATE_LOCATION_GROUP_SELECT_ALL_DEVICES(locationGroup));
		} catch (error: any) {
		}
	};

  export const updateMainLocationGroupNoOfTimes =
	(location: LocationGroupCampaignSearch, noOfTimes: number) =>
	async (dispatch: any) => {
		try {
			dispatch(
				UPDATE_LOCATION_GROUP_MANUAL_NO_TIMES({
					locationGroup: location,
					noOfTimes: noOfTimes,
				}),
			);
		} catch (error: any) {
		}
	};

  export const addCampaignManualLocationAction =
	(location: MainLocationAllocation) => async (dispatch: any) => {
		try {
			dispatch(CAMPAIGN_MANUAL_ADD_LOCATION(location));
		} catch (error: any) {
		}
	};

  export const updateMainLocationSelectAllDevices =
	(location: MainLocationAllocation) => async (dispatch: any) => {
		try {
			dispatch(UPDATE_LOCATION_SELECT_ALL_DEVICES(location));
		} catch (error: any) {
		}
	};


export const updateMainLocationNoOfTimes = (location: MainLocationAllocation,
    noOfTimes: number,) => async (dispatch: any) => {
        try {
            dispatch(UPDATE_LOCATION_MANUAL_NO_TIMES({
                location,
                noOfTimes
            }))
        } catch (error: any) {
        }
    }

export const getMainLocationManualResult = (requestObject: any): any => async (
    dispatch: any,
) => {
    try {
        dispatch(SET_PROGRESS(true));
        const slotResults: SlotResults = await getManualSlotAllocation(requestObject);
        dispatch(GET_MAIN_LOCATION_MANUAL_RESULT(slotResults));
        dispatch(SET_PROGRESS(false));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const getLocationSearchByArea = (requestObject: any): any => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        const searchResults: Array<MainLocationAllocation> = await getAllLocationDetails(
            requestObject,
        );
        if (searchResults.length) {
            dispatch(GET_MAIN_LOCATIONS_SEARCH_BY_AREA(searchResults));
            dispatch(
                UPDATE_CAMPAIGN_AD_UI_STATE({
                    toolbarMode: 'row',
                    mainContainerAlign: 'center',
                    toolbarColSize: 12,
                }),
            );
        } else {
            dispatch(SEND_MESSAGE({ message: 'No Locations available', type: 'warning' }));
        }
        dispatch(UPDATE_CAMPAIGN_AD_UI_STATE({ tabValue: 0, showConfirm: false }));
        dispatch(SET_PROGRESS(false));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ messange: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const getLocationGroupManualResult = (requestObject: any): any => async (
    dispatch: any,
) => {
    if (requestObject && requestObject.locations && requestObject.locations.length > 0) {
        try {
            dispatch(SET_PROGRESS(true));
            const slotResults: SlotResults = await getLocationGroupManualSlotAllocation(
                requestObject,
            );
            dispatch(GET_LOCATION_GROUP_MANUAL_RESULT(slotResults));
            dispatch(SET_PROGRESS(false));
        } catch (error: any) {
            dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
            dispatch(SET_PROGRESS(false));
        }
    } else {
        dispatch(SEND_MESSAGE({ message: 'Add Locations to continue', type: 'warning' }));
    }
};
export const getLocationGroupSearchByCity = (requestObject: any): any => async (
    dispatch: any,
) => {
    try {
        dispatch(SET_PROGRESS(true));
        const searchResults: Array<LocationGroupCampaignSearch> = await getLocationGroupSearchResults(
            requestObject,
        );
        if (searchResults.length) {
            dispatch(GET_LOCATION_GROUP_SEARCH_BY_CITY(searchResults));
            dispatch(SET_PROGRESS(false));
            dispatch(
                UPDATE_CAMPAIGN_AD_UI_STATE({
                    toolbarMode: 'row',
                    mainContainerAlign: 'center',
                    toolbarColSize: 12,
                }),
            );
        } else {
            dispatch(SEND_MESSAGE({ message: 'No Locations available', type: 'warning' }));
        }
        dispatch(UPDATE_CAMPAIGN_AD_UI_STATE({ tabValue: 0, showConfirm: false }));
    } catch (error: any) {
        console.log("error----------------->>", error);

        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const deleteCampaignAdvertisement = (index: number, uuid: string): any => async (
    dispatch: any,
) => {
    try {
        dispatch(SET_PROGRESS(true));
        await deleteSlotAllocation(uuid);
        dispatch(SET_PROGRESS(false));
        dispatch(DELETE_CAMPAIGN_ADVERTISEMENT(index));
        dispatch(SEND_MESSAGE({ message: 'DELETED SUCCESSFULLY', type: 'success' }));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const getCitiesDetails = (): any => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        const cities: Array<string> = await getCities();
        dispatch(GET_CITIES(cities));
        dispatch(SET_PROGRESS(false));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const deleteBlockedSlot = (uuid: any): any => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        await deleteSlotAllocation(uuid);
        dispatch(RESET_CAMPAIGN_ADVERTISEMENT_FORM(null));
        dispatch(RESET_CAMPAIGN_ADVERTISEMENT_UI_STATE(null));
        dispatch(SET_PROGRESS(false));
        dispatch(SEND_MESSAGE({ message: 'DELETED SUCCESSFULLY', type: 'success' }));
    } catch (error: any) {
        dispatch(SET_PROGRESS(false));
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
    }
};

export const getAreasByCitiesDetails = (cities: any): any => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        const areas: Array<string> = await getAreas(cities);
        dispatch(GET_AREAS_BY_CITIES(areas));
        dispatch(SET_PROGRESS(false));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }))
        dispatch(SET_PROGRESS(false));
    }
};
export const getCampaignAdvertisements = (): any => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        const advertisements: Array<Advertisement> = await getCampaignAdvertisementDetails();
        dispatch(GET_CAMPAIGN_ADVERTISEMENTS(advertisements));
        dispatch(SET_PROGRESS(false));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const getAdvertisementsByClientIdAction = (clientId: number): any => async (
    dispatch: any,
) => {
    try {
        const advertisements: Array<Advertisement> = await advertisementDetailsbyClientId(
            clientId,
        );
        dispatch(GET_ADVERTISEMENT_DATA_LIST_BY_CLIENT_ID(advertisements));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
    }
};
export const getCampaignDetailsById = (campaignId: number): any => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        const campaign: Campaign = await getCampaignDetails(campaignId);
        dispatch(GET_CAMPAIGN_BY_ID(campaign));
        dispatch(SET_PROGRESS(false));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const updateCampaignAction = (campaign: Campaign) => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        await updateCampaignDetails(campaign);
        dispatch(SET_PROGRESS(false));
        dispatch(SEND_MESSAGE({ message: 'UPDATED SUCCESSFULLY', type: 'success' }));
        setTimeout(() => {
            history.back();
            // navigate(routes.CAMPAIGN_LIST.path)
        }, 2000);
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};


export const createCampaignAction = (campaign: Campaign) => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        await submitCampaignDetails(campaign);
        dispatch(SET_PROGRESS(false));
        dispatch(SEND_MESSAGE({ message: 'CREATED SUCCESSFULLY', type: 'success' }));
        setTimeout(() => {
            history.back();
            // navigate(routes.CAMPAIGN_LIST.path)
        }, 2000);
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};
export const updateCampaignFormAction =
    (partialCampaign: Partial<Campaign>) => async (dispatch: any) => {
        try {
            dispatch(UPDATE_CAMPAIGN_FORM(partialCampaign));
        } catch (error: any) {
            dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        }
    };

export const getAllCampaignDetails =
    (
        pageIndex = 0,
        pageSize = 10,
        filterRequestObject: any = [],
        sortRequestObject: any = [],
    ) =>
        async (dispatch: any) => {
            try {
                const campaigns = await getAllCampaigns(
                    pageIndex,
                    pageSize,
                    filterRequestObject,
                    sortRequestObject,
                );
                dispatch(GET_CAMPAIGN_DATA_LIST(campaigns));
            } catch (error: any) {
                dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
            }
        };



export const resetCampaignState = () => async (dispatch: any) => {
    try {
        dispatch(RESET_CAMPAIGN_STATE(null));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
    }
};

export const deleteCampaignById = (campaignId: number) => async (dispatch: any) => {
    try {
        dispatch(SET_PROGRESS(true));
        await deleteCampaign(campaignId);
        dispatch(SET_PROGRESS(false));
        dispatch(SEND_MESSAGE({ message: 'DELETED SUCCESSFULLY', type: 'success' }));
        dispatch(getAllCampaignDetails());
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
        dispatch(SET_PROGRESS(false));
    }
};



export const updateCampaignMode = (selectedId: number, mode: ACCESS_MODE) => async (dispatch: any) => {
    try {
        dispatch(UPDATE_CAMPAIGN_MODE({ selectedId, mode }));
    } catch (error: any) {
        dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
    }
};


export const resetCampaignAdvertisementAction = () => async (dispatch: any) => {
	try {
		dispatch(RESET_CAMPAIGN_ADVERTISEMENT_FORM(null));
	} catch (error: any) {
		dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
	}
};

export const resetCampaignAdvertisementUIStateAction = () => async (dispatch: any) => {
	try {
		dispatch(RESET_CAMPAIGN_ADVERTISEMENT_UI_STATE(null));
	} catch (error: any) {
		dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
	}
};


export const updateCampaignAdvertisementFormAction =
	(payload: Partial<CampaignAdvertisement>) => async (dispatch: any) => {
		try {
			dispatch(UPDATE_CAMPAIGN_ADVERTISEMENT_FORM(payload));
		} catch (error: any) {
			dispatch(SEND_MESSAGE({ message: error, type: 'error' }));
		}
	};
